import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { User, Building2, Globe } from 'lucide-react'
import { AssetDetail } from '@/modules/Asset/types'
import { Badge } from '@/components/ui/badge'
import {
    getAllDepartments,
    getEmployeeContains,
} from '@/modules/Employee/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'

interface ShareTabProps {
    asset: AssetDetail
}

const ShareTab = ({ asset }: ShareTabProps) => {
    const { t } = useTranslation('asset')

    // Fetch departments to map IDs to names
    const { data: departmentsData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DEPARTMENTS],
        queryFn: getAllDepartments,
    })

    // Fetch employees to map IDs to names
    const { data: employeesData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEES, asset.shared_employee_ids],
        queryFn: () =>
            getEmployeeContains({
                employee_ids: asset.shared_employee_ids || [],
            }),
        enabled: (asset.shared_employee_ids?.length || 0) > 0,
    })

    const hasSharedEmployees =
        asset.shared_employee_ids && asset.shared_employee_ids.length > 0
    const hasSharedDepartments =
        asset.shared_department_ids && asset.shared_department_ids.length > 0

    const isSharedWithEveryone = !hasSharedEmployees && !hasSharedDepartments

    // Helper to get department name by ID
    const getDepartmentName = (deptId: number) => {
        const dept = departmentsData?.results?.find(
            (d) => d.department_id === deptId
        )
        return dept?.department_name || `ID: ${deptId}`
    }

    // Helper to get employee name by ID
    const getEmployeeName = (empId: number) => {
        const emp = employeesData?.find((e) => e.value === empId)
        return emp?.label || `ID: ${empId}`
    }

    return (
        <Card className="border-none">
            <CardHeader>
                <CardTitle>
                    <Typogaphy className="font-semibold text-primary mb-2">
                        {t('share.sharedWith')}
                    </Typogaphy>
                </CardTitle>
            </CardHeader>
            <CardContent>
                {isSharedWithEveryone ? (
                    <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <Globe className="h-5 w-5 text-blue-600" />
                        <p className="text-sm text-blue-900 font-medium">
                            {t('share.sharedWithEveryone')}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {hasSharedDepartments && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                    <Building2 className="h-4 w-4" />
                                    {t('share.departments')} (
                                    {asset.shared_department_ids?.length})
                                </h3>
                                <div className="space-y-2">
                                    {asset.shared_department_ids?.map(
                                        (deptId) => (
                                            <div
                                                key={deptId}
                                                className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded border"
                                            >
                                                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center border border-blue-200">
                                                    <Building2 className="h-4 w-4 text-blue-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">
                                                        {getDepartmentName(
                                                            deptId
                                                        )}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {t(
                                                            'share.departmentAccess'
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        )}

                        {hasSharedEmployees && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                    <User className="h-4 w-4" />
                                    {t('share.employees')} (
                                    {asset.shared_employee_ids?.length})
                                </h3>
                                <div className="space-y-2">
                                    {asset.shared_employee_ids?.map((empId) => (
                                        <div
                                            key={empId}
                                            className="flex items-center justify-between p-3 hover:bg-gray-50 rounded border"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center border">
                                                    <User className="h-4 w-4 text-gray-500" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">
                                                        {getEmployeeName(empId)}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {t(
                                                            'share.individualAccess'
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <Badge
                                                variant="outline"
                                                className="text-xs"
                                            >
                                                {t('share.viewer')}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export default ShareTab
